import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CREATE_FAVORITE_SHOP,
  DESTROY_FAVORITE_SHOP,
} from '@/api/mutations/shopFavorite.mutations';
import { useToastStore } from '@/store/useToastStore';

interface Shop {
  id: string;
  name: string;
  isFavorited?: boolean;
}

export function useShopFavorite({ shop }: { shop?: Shop }) {
  const { t } = useTranslation();
  const [isFavorited, setIsFavorited] = useState(false);
  const openToast = useToastStore(state => state.openToast);
  const closeToast = useToastStore(state => state.closeToast);
  const onAnimationComplete = useToastStore(state => state.onAnimationComplete);

  useEffect(() => {
    if (shop) {
      setIsFavorited(shop.isFavorited ?? false);
    }
  }, [shop]);

  const [createFavorite, { loading: createLoading }] = useMutation(
    CREATE_FAVORITE_SHOP,
    {
      onError: () => setIsFavorited(false),
      update: cache => {
        if (!shop?.id) return;
        cache.modify({
          id: cache.identify({ __typename: 'Shop', id: shop.id }),
          fields: {
            isFavorited: () => true,
          },
          broadcast: true,
        });
      },
    }
  );

  const [destroyFavorite, { loading: destroyLoading }] = useMutation(
    DESTROY_FAVORITE_SHOP,
    {
      onError: () => setIsFavorited(true),
      update: cache => {
        if (!shop?.id) return;
        cache.modify({
          id: cache.identify({ __typename: 'Shop', id: shop.id }),
          fields: {
            isFavorited: () => false,
          },
          broadcast: true,
        });
      },
    }
  );

  const toggleFavorite = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!shop) return;

    const shopId = shop.id;
    const shopName = shop.name;

    closeToast();
    onAnimationComplete();

    if (isFavorited) {
      setIsFavorited(false);
      setTimeout(() => {
        openToast({
          text: t('shop.delete_bookmark_shop', { name: shopName }),
        });
      }, 50);
      destroyFavorite({ variables: { shopId } });
    } else {
      setIsFavorited(true);
      setTimeout(() => {
        openToast({
          text: t('shop.add_bookmark_shop', { name: shopName }),
        });
      }, 50);
      createFavorite({ variables: { shopId } });
    }
  };

  return {
    isFavorited,
    toggleFavorite,
    loading: createLoading || destroyLoading,
  };
}
